import { BuyXPayYOffer } from '../../promotion-display-utils';
import { ProductAggregate } from '../aggregate-cart';
import { buildFamilyOutcome, FamilyOutcome, UnitDiscount } from '../family-outcome.types';

interface OfferCount {
  offer: BuyXPayYOffer;
  count: number;
}

interface Distribution {
  instances: OfferCount[];
  freedUnits: number;
  usedUnits: number;
}

/**
 * Computes the buyXPayYOffers-family outcome over a product aggregate.
 *
 * - Solves the optimal offer distribution via unbounded-knapsack DP
 *   (`solveOptimalDistribution`).
 * - The freed amount is taken from the K cheapest units globally in the aggregate
 *   (rearrangement: more generous units get freed, anti-gaming).
 * - Each offer instance "claims" the next (buy - pay) units from the K-cheapest pool
 *   in deterministic source-promotion order; that share is attributed to its
 *   sourcePromotionId.
 *
 * Pure and deterministic.
 */
export function calculateBuyXPayYForAggregate(
  buyXPayYOffers: BuyXPayYOffer[],
  aggregate: ProductAggregate,
): FamilyOutcome {
  const best = solveOptimalDistribution(buyXPayYOffers, aggregate.totalUnits);
  if (!best || best.freedUnits === 0) {
    return { family: 'buyXPayY', totalDiscount: 0, perPromoBreakdown: [] };
  }

  const freedUnitsSlice = aggregate.units.slice(0, best.freedUnits);

  const sortedInstances = [...best.instances].sort((a, b) =>
    a.offer.sourcePromotionId.localeCompare(b.offer.sourcePromotionId),
  );

  const unitDiscountsBySource = new Map<string, UnitDiscount[]>();
  let cursor = 0;
  for (const instance of sortedInstances) {
    const freedPerInstance = (instance.offer.buy - instance.offer.pay) * instance.count;
    const claimed = freedUnitsSlice.slice(cursor, cursor + freedPerInstance);
    cursor += freedPerInstance;

    const source = instance.offer.sourcePromotionId;
    let bucket = unitDiscountsBySource.get(source);
    if (!bucket) {
      bucket = [];
      unitDiscountsBySource.set(source, bucket);
    }
    for (const unit of claimed) {
      bucket.push({
        unit,
        amount: unit.basePrice,
        sourcePromotionId: source,
      });
    }
  }

  return buildFamilyOutcome('buyXPayY', unitDiscountsBySource);
}

/**
 * Finds the optimal buyXPayY distribution via unbounded-knapsack DP.
 *
 * State: `dp[u]` carries the lex-max `(freedUnits, usedUnits)` achievable using
 * up to `u` units. The lex order encodes the briefing's tiebreakers:
 *   - Primary: max `freedUnits` (more savings).
 *   - Secondary: max `usedUnits` (equivalent to min `looseUnits` since
 *     `loose = totalUnits − used`).
 *
 * Determinism: offers are sorted by `sourcePromotionId` before the DP loop, so
 * ties on the inner update pick the lexicographically earliest source (matches
 * the prior behavior of the enumerate+signature-sort approach).
 *
 * Complexity: `O(totalUnits × N)` time, `O(totalUnits)` memory.
 *
 * Replaces an earlier exhaustive enumeration that was `O((Q/buy)^N)` — a 50-unit
 * cart with 5 combo offers hit ~150 000 paths and froze the UI; an accidental
 * extra zero in a quantity counter (e.g. 100 instead of 10) made it worse.
 */
function solveOptimalDistribution(
  offers: BuyXPayYOffer[],
  totalUnits: number,
): Distribution | null {
  const usable = offers.filter(o => o.buy >= 2 && o.buy - o.pay > 0);
  if (usable.length === 0 || totalUnits <= 0) {
    return null;
  }

  const sortedOffers = [...usable].sort((a, b) =>
    a.sourcePromotionId.localeCompare(b.sourcePromotionId),
  );

  const dpFreed = new Int32Array(totalUnits + 1);
  const dpUsed = new Int32Array(totalUnits + 1);
  const chosen = new Int32Array(totalUnits + 1).fill(-1);

  for (let u = 1; u <= totalUnits; u++) {
    dpFreed[u] = dpFreed[u - 1];
    dpUsed[u] = dpUsed[u - 1];

    for (let i = 0; i < sortedOffers.length; i++) {
      const offer = sortedOffers[i];
      if (u < offer.buy) {
        continue;
      }
      const prev = u - offer.buy;
      const candidateFreed = dpFreed[prev] + (offer.buy - offer.pay);
      const candidateUsed = dpUsed[prev] + offer.buy;

      if (
        candidateFreed > dpFreed[u] ||
        (candidateFreed === dpFreed[u] && candidateUsed > dpUsed[u])
      ) {
        dpFreed[u] = candidateFreed;
        dpUsed[u] = candidateUsed;
        chosen[u] = i;
      }
    }
  }

  if (dpFreed[totalUnits] === 0) {
    return null;
  }

  const countByIndex = new Map<number, number>();
  let u = totalUnits;
  while (u > 0) {
    const offerIndex = chosen[u];
    if (offerIndex < 0) {
      u -= 1;
      continue;
    }
    countByIndex.set(offerIndex, (countByIndex.get(offerIndex) ?? 0) + 1);
    u -= sortedOffers[offerIndex].buy;
  }

  const instances: OfferCount[] = [];
  for (const [idx, count] of countByIndex) {
    instances.push({ offer: sortedOffers[idx], count });
  }

  return {
    instances,
    freedUnits: dpFreed[totalUnits],
    usedUnits: dpUsed[totalUnits],
  };
}
